import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { Tooltip } from 'react-tooltip';
import Switch from 'react-switch';
import PageTitle from '../layouts/PageTitle';
import { useLoggedInUserContextProvider } from '../../context/LoggedInUserContext';
import { getUserRoles } from '../../utils/getUserRoles';
import Span from './Forms/Elements/Span';
import { useStaticCollections } from '../../context/StaticCollections';
import { useAdminActionCollections } from '../../context/AdminActionCollections';
import { useRefereeCollections } from '../../context/RefereeReportCollections';
import { axiosGet } from '../../utils/axiosGet';
import { axiosPut } from '../../utils/axiosPut';
import { axiosPost } from '../../utils/axiosPost';
import { isProduction } from '../../utils/constants';
import { devLog, devError } from '../../utils/devLog';
import ModalConfirmation from './ModalConfirmation';
import NominationCSVExport from './NominationCSVExport';
import NominationZipExport from './NominationZipExport';
import PendingRefereesAlert from './PendingRefereesAlert';
import { usePendingNonAasReferees } from '../hooks/usePendingNonAasReferees';
import { arrayToCSV } from '../../utils/arrayToCsv';
import 'react-tooltip/dist/react-tooltip.css';

const TOGGLE_TOOLTIPS = {
  toggleNewSubmission: 'Allow proposers to create new nominations',
  toggleSubmissionEdits: 'Allow proposers to edit existing nominations',
  toggleRefereeReports: 'Allow referees to upload their reports',
  toggleCommitteeAccess: 'Allow committee members to access nominations',
  toggleCOI: 'Enable conflict of interest declarations and checks',
  toggleScoring: 'Enable committee scoring of candidates',
  toggleCouncilMembers: 'Enable council member roles and visibility',
  toggleRecommendedToCouncil:
    'Show the Recommended to Council button and filters',
  toggleRefereeReminders:
    'Enable automatic reminder emails to referees who have not submitted their reports',
};

const AdminActions = () => {
  const { userDetails, setUserDetails } = useLoggedInUserContextProvider();

  const {
    usersArray,
    nonAasRefereesArray,
    titlesTypes,
    refreshUsersByNonAasReferees,
    addNonAasReferee,
  } = useStaticCollections();

  const { refereeReports, refreshRefereeReportsByUserIds } =
    useRefereeCollections();

  const { refreshAdminAction } = useAdminActionCollections();

  const [selectedUser, setSelectedUser] = useState(null);
  const [notification, setNotification] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const [enableSubmissionEdit, setEnableSubmissionEdit] = useState(false);
  const [enableRefereeReportUploads, setEnableRefereeReportUploads] =
    useState(false);
  const [enableNewSubmission, setEnableNewSubmission] = useState(false);
  const [enableCommitteeAccess, setEnableCommitteeAccess] = useState(false);
  const [enableCOI, setEnableCOI] = useState(false);
  const [enableScoring, setEnableScoring] = useState(false);
  const [enableCouncilMembers, setEnableCouncilMembers] = useState(false);
  const [enableRecommendedToCouncil, setEnableRecommendedToCouncil] =
    useState(false);
  const [enableRefereeReminders, setEnableRefereeReminders] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [chairReportDeadline, setChairReportDeadline] = useState('');

  const [showAnonymizeModal, setShowAnonymizeModal] = useState(false);
  const [anonymizeLoading, setAnonymizeLoading] = useState(false);
  const [anonymizeError, setAnonymizeError] = useState(null);

  const {
    pendingReferees,
    pendingCount,
    isLoading: isLoadingPendingReferees,
  } = usePendingNonAasReferees(
    usersArray,
    nonAasRefereesArray,
    refereeReports,
    refreshUsersByNonAasReferees,
    refreshRefereeReportsByUserIds,
    addNonAasReferee,
  );

  useEffect(() => {
    let cancelled = false;
    const loadAdminAction = async () => {
      try {
        const response = await axiosGet('admin-action');
        if (cancelled) return;
        const attrs = response?.data?.data?.attributes;
        if (!attrs) return;
        setEnableSubmissionEdit(!!attrs.enableSubmissionEdit);
        setEnableRefereeReportUploads(!!attrs.enableRefereeReportUploads);
        setEnableNewSubmission(!!attrs.enableNewSubmission);
        setEnableCommitteeAccess(!!attrs.enableCommitteeAccess);
        setEnableCOI(!!attrs.enableCOI);
        setEnableScoring(!!attrs.enableScoring);
        setEnableCouncilMembers(!!attrs.enableCouncilMembers);
        setEnableRecommendedToCouncil(!!attrs.enableRecommendedToCouncil);
        setEnableRefereeReminders(!!attrs.enableRefereeReminders);
      } catch (error) {
        devError('Error loading admin action:', error);
      } finally {
        if (!cancelled) setIsLoaded(true);
      }
    };
    loadAdminAction();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fetchDeadline = async () => {
      try {
        const response = await axiosGet('chair-report-deadline');
        const record = response?.data?.data;
        if (record?.attributes?.deadline) {
          const date = new Date(record.attributes.deadline);
          setChairReportDeadline(date.toISOString().split('T')[0]);
        }
      } catch (e) {
        devLog(
          '[AdminActions] Chair report deadline endpoint not available yet',
        );
      }
    };

    fetchDeadline();
  }, []);

  const selectOptions = usersArray
    .filter((user) => !user.blocked)
    .map((user) => ({
      value: user.id,
      label: user.username,
    }));

  const handleChange = (selectedOption) => {
    setSelectedUser(selectedOption);
  };

  const masqueradeAsUser = async (selectedUserId) => {
    if (!selectedUserId) {
      return;
    }

    const masqueradeUser = usersArray.find(
      (user) => user.id === selectedUserId,
    );

    if (typeof masqueradeUser === 'object') {
      const userRoles = await getUserRoles(masqueradeUser);
      const hasAdminOrSuperAdmin = userRoles.some(
        (role) => role === 'admin' || role === 'super-admin',
      );

      masqueradeUser.email = userDetails.email;

      if (!hasAdminOrSuperAdmin) {
        setUserDetails({
          ...masqueradeUser,
          roles: userRoles,
          isMasquerade: true,
        });

        setNotification('Updated');
      } else {
        setErrorMessage('Masquerading as an admin is not allowed');
      }
    }
  };

  const exportPendingUsers = () => {
    if (!pendingReferees || pendingReferees.length === 0) {
      return;
    }

    const exportData = pendingReferees.map((referee) => {
      const user = usersArray.find((u) => u.nonAasRefereeId === referee.id);
      const title = titlesTypes?.[referee.attributes?.titleId] || '';
      const firstName = referee.attributes?.name || '';
      const lastName = referee.attributes?.lastName || '';
      const fullName =
        `${title ? `${title} ` : ''}${firstName} ${lastName}`.trim();
      return {
        'Referee ID': referee.id,
        'User ID': user?.id || '',
        Title: title,
        'First Name': firstName,
        'Last Name': lastName,
        'Full Name': fullName,
        Email: referee.attributes?.email || user?.email || '',
        Organisation: referee.attributes?.organisation || '',
        Memberships: referee.attributes?.memberships || '',
        Username: user?.username || '',
        'Account Status': user?.blocked
          ? 'Blocked (Awaiting Activation)'
          : 'Active',
      };
    });

    const csvContent = arrayToCSV(exportData);
    const blob = new Blob([`\uFEFF${csvContent}`], {
      type: 'text/csv;charset=utf-8;',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    const dateStr = new Date().toISOString().split('T')[0];
    link.download = `independent_assessors_awaiting_activation_${dateStr}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const saveChairReportDeadline = async () => {
    try {
      await axiosPut('chair-report-deadline', {
        data: {
          deadline: chairReportDeadline
            ? new Date(chairReportDeadline).toISOString()
            : null,
        },
      });
    } catch (error) {
      devError('Error saving chair report deadline:', error);
      throw error;
    }
  };

  const saveAdminAction = async () => {
    try {
      const response = await axiosPut(`admin-action`, {
        data: {
          enableSubmissionEdit: Boolean(enableSubmissionEdit),
          enableNewSubmission: Boolean(enableNewSubmission),
          enableRefereeReportUploads: Boolean(enableRefereeReportUploads),
          enableCommitteeAccess: Boolean(enableCommitteeAccess),
          enableCOI: Boolean(enableCOI),
          enableScoring: Boolean(enableScoring),
          enableCouncilMembers: Boolean(enableCouncilMembers),
          enableRecommendedToCouncil: Boolean(enableRecommendedToCouncil),
          enableRefereeReminders: Boolean(enableRefereeReminders),
        },
      });

      if (response.status === 200) {
        await refreshAdminAction();
      }
    } catch (error) {
      devError('Error saving admin action buttons', error);
      throw error;
    }
  };

  const saveAllSettings = async () => {
    try {
      await Promise.all([saveAdminAction(), saveChairReportDeadline()]);
      setNotification('Settings saved successfully.');
    } catch (error) {
      setErrorMessage('Failed to save settings. Please try again.');
    }
  };

  const anonymizeData = async () => {
    setAnonymizeLoading(true);
    setAnonymizeError(null);
    try {
      const response = await axiosPost(
        'anonymize-data/anonymize-all',
        undefined,
        { timeout: 600000 },
      );

      if (response.status === 200) {
        setNotification('Successfully anonymized all personal data');
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      devError('Error anonymizing data:', error);
      setAnonymizeError('Failed to anonymize data. Please try again.');
    } finally {
      setAnonymizeLoading(false);
    }
  };

  const handleSaveClick = () => {
    setShowModal(true);
  };

  const handleAnonymizeClick = () => {
    setShowAnonymizeModal(true);
  };

  const handleConfirmSelect = () => {
    saveAllSettings();
    setShowModal(false);
  };

  const handleCancelSelect = () => {
    setShowModal(false);
  };

  const handleConfirmAnonymize = () => {
    anonymizeData();
    setShowAnonymizeModal(false);
  };

  const handleCancelAnonymize = () => {
    setShowAnonymizeModal(false);
  };

  return (
    <>
      <PageTitle
        activeMenu='Admin Actions'
        motherMenu='Home'
        notificationMessage={notification}
      />
      <div>
        <div className='row mb-3'>
          <div className='col-md-6 mb-3'>
            <div className='card h-100 d-flex flex-column admin-card'>
              <div className='card-header'>
                <h4 className='card-title'>Masquerade</h4>
              </div>
              <div className='card-body flex-grow-1'>
                <label htmlFor='user-select'>Masquerade as user</label>
                <Select
                  id='user-select'
                  value={selectedUser}
                  onChange={handleChange}
                  options={selectOptions}
                />
                <div className='mt-xl-2'>
                  <Span name='user-select' text={errorMessage} />
                </div>
                {isLoadingPendingReferees ? (
                  <div className='d-flex align-items-center mt-3'>
                    <div
                      className='spinner-border spinner-border-sm text-primary me-2'
                      role='status'
                    >
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                    <span>Identifying blocked users...</span>
                  </div>
                ) : (
                  <PendingRefereesAlert
                    pendingReferees={pendingReferees}
                    pendingCount={pendingCount}
                  />
                )}
              </div>
              <div className='mt-auto text-end p-3 d-flex justify-content-end gap-2 flex-wrap'>
                <button
                  type='button'
                  className='btn btn-primary standard-width-button'
                  onClick={exportPendingUsers}
                  disabled={!pendingReferees || pendingReferees.length === 0}
                  title={
                    pendingCount > 0
                      ? 'Export Independent Assessors Awaiting Activation'
                      : 'No users awaiting activation'
                  }
                >
                  <i className='bi bi-download' /> Export Unapproved Referees
                </button>
                <button
                  type='button'
                  className='btn btn-success standard-width-button'
                  onClick={() =>
                    selectedUser && masqueradeAsUser(selectedUser.value)
                  }
                >
                  Submit
                </button>
              </div>
            </div>
          </div>

          <div className='col-md-6 mb-3'>
            <div className='card h-100 d-flex flex-column admin-card'>
              <div className='card-header'>
                <h4 className='card-title'>System Settings</h4>
              </div>
              <div className='card-body flex-grow-1'>
                {[
                  {
                    id: 'toggleNewSubmission',
                    label: 'New Submissions',
                    checked: enableNewSubmission,
                    onChange: () =>
                      setEnableNewSubmission(!enableNewSubmission),
                  },
                  {
                    id: 'toggleSubmissionEdits',
                    label: 'Submission Edits',
                    checked: enableSubmissionEdit,
                    onChange: () =>
                      setEnableSubmissionEdit(!enableSubmissionEdit),
                  },
                  {
                    id: 'toggleRefereeReports',
                    label: 'Referee Report Uploads',
                    checked: enableRefereeReportUploads,
                    onChange: () =>
                      setEnableRefereeReportUploads(
                        !enableRefereeReportUploads,
                      ),
                  },
                  {
                    id: 'toggleCommitteeAccess',
                    label: 'Committee Access',
                    checked: enableCommitteeAccess,
                    onChange: () =>
                      setEnableCommitteeAccess(!enableCommitteeAccess),
                  },
                  {
                    id: 'toggleCOI',
                    label: 'COI Functionality',
                    checked: enableCOI,
                    onChange: () => setEnableCOI(!enableCOI),
                  },
                  {
                    id: 'toggleScoring',
                    label: 'Scoring Functionality',
                    checked: enableScoring,
                    onChange: () => setEnableScoring(!enableScoring),
                  },
                  {
                    id: 'toggleCouncilMembers',
                    label: 'Council Members',
                    checked: !!enableCouncilMembers,
                    onChange: () =>
                      setEnableCouncilMembers(!enableCouncilMembers),
                  },
                  {
                    id: 'toggleRecommendedToCouncil',
                    label: 'Recommended to Council',
                    checked: !!enableRecommendedToCouncil,
                    onChange: () =>
                      setEnableRecommendedToCouncil(
                        !enableRecommendedToCouncil,
                      ),
                  },
                  {
                    id: 'toggleRefereeReminders',
                    label: 'Referee Reminders',
                    checked: !!enableRefereeReminders,
                    onChange: () =>
                      setEnableRefereeReminders(!enableRefereeReminders),
                  },
                ].map((toggle) => (
                  <div
                    key={toggle.id}
                    className='d-flex align-items-center mt-3'
                  >
                    <Switch
                      onChange={toggle.onChange}
                      checked={toggle.checked}
                      onColor='#4cd964'
                      offColor='#e5e5ea'
                      handleDiameter={20}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      height={24}
                      width={48}
                      id={toggle.id}
                    />
                    <label
                      className='form-check-label mb-0 ms-2'
                      htmlFor={toggle.id}
                    >
                      {toggle.label}
                      <i
                        className='fas fa-info-circle ms-2 text-muted'
                        style={{ fontSize: '0.85em', cursor: 'pointer' }}
                        data-tooltip-id='admin-toggle-tooltip'
                        data-tooltip-content={TOGGLE_TOOLTIPS[toggle.id]}
                      />
                    </label>
                  </div>
                ))}
                <Tooltip id='admin-toggle-tooltip' place='top' />

                <hr className='my-3' />

                <div>
                  <label
                    htmlFor='chairReportDeadline'
                    className='form-label mb-1'
                  >
                    Chair&apos;s Report Deadline
                  </label>
                  <div className='d-flex align-items-center gap-2'>
                    <input
                      type='date'
                      id='chairReportDeadline'
                      className='form-control'
                      style={{ maxWidth: '220px' }}
                      value={chairReportDeadline}
                      onChange={(e) => setChairReportDeadline(e.target.value)}
                    />
                    {chairReportDeadline && (
                      <button
                        type='button'
                        className='btn btn-sm btn-outline-secondary'
                        onClick={() => setChairReportDeadline('')}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <small className='text-muted'>
                    Chairs cannot edit their report after this date.
                  </small>
                </div>
              </div>
              <div className='mt-auto text-end p-3'>
                <button
                  type='button'
                  className='btn btn-success standard-width-button'
                  onClick={handleSaveClick}
                  disabled={!isLoaded}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>

          <NominationCSVExport />
          <NominationZipExport />

          {!isProduction && (
            <div className='col-md-6 mb-3'>
              <div className='card h-100 d-flex flex-column admin-card'>
                <div className='card-header'>
                  <h4 className='card-title'>Data Anonymization</h4>
                </div>
                <div className='card-body flex-grow-1'>
                  {anonymizeError && (
                    <div className='alert alert-danger mb-3' role='alert'>
                      {anonymizeError}
                    </div>
                  )}
                  <p className='mb-3'>This will anonymize the following:</p>
                  <ul className='mb-3'>
                    <li>
                      <strong>Candidates:</strong> Names and last names replaced
                      with dummy values
                    </li>
                    <li>
                      <strong>Fellows:</strong> Names, last names, and emails
                      replaced with dummy values
                    </li>
                    <li>
                      <strong>Referees:</strong> Names, last names, and emails
                      replaced with dummy values
                    </li>
                    <li>
                      <strong>Users:</strong> Usernames updated to reflect
                      anonymized data
                    </li>
                  </ul>
                  <div className='alert alert-warning' role='alert'>
                    <small>
                      ⚠️ <strong>Warning:</strong> This helps take view
                      snapshots instead of creating nominations from scratch,
                      but detailed information (like nomination view proposer
                      data) remains unchanged.
                    </small>
                  </div>
                </div>
                <div className='mt-auto text-end p-3'>
                  <button
                    type='button'
                    className='btn btn-success standard-width-button'
                    onClick={handleAnonymizeClick}
                    disabled={anonymizeLoading}
                  >
                    {anonymizeLoading ? 'Processing...' : 'Anonymize Data'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ModalConfirmation
        showModal={showModal}
        handleCancelSelect={handleCancelSelect}
        bodyText='Are you sure you want to save?'
        handleConfirmSelect={handleConfirmSelect}
        setShowModal={setShowModal}
      />

      <ModalConfirmation
        showModal={showAnonymizeModal}
        handleCancelSelect={handleCancelAnonymize}
        bodyText='Are you sure you want to anonymize all candidates, fellows, referees, and users? This cannot be undone.'
        handleConfirmSelect={handleConfirmAnonymize}
        setShowModal={setShowAnonymizeModal}
      />
    </>
  );
};

export default AdminActions;
